import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetMany } from "react-admin";
import { Box, Button, Chip, Popover, Stack, Typography } from "@mui/material";
import { FilterList as FilterIcon } from "@mui/icons-material";
import { ClientStatus, ResourceName, type Client, type Policy, type Vendor } from "@/types";
import { MultiSelectFilter } from "../shared/components/MultiSelectFilter";

interface PolicyDashboardFiltersProps {
  allPolicies: Policy[];
  filters: Record<string, string | string[]>;
}

const FILTER_LABELS: Record<string, string> = {
  client_id_ref: "Client",
  vendor_id_ref: "Carrier",
  tpa_vendor_ids: "TPA",
  rx_vendor_ids: "Rx/PBM",
};

/**
 * Popover filter panel with full cascading: every filter narrows the options in all other filters.
 * Pinned to the right of the tabs row. Active filters shown as dismissible chips.
 */
export const PolicyDashboardFilters = ({ allPolicies, filters }: PolicyDashboardFiltersProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  // -- Collect all unique IDs for data fetching --
  const { insuranceProviderIds, tpaVendorIds, rxVendorIds, clientIds } = useMemo(
    () => ({
      insuranceProviderIds: Array.from(
        new Set(allPolicies.map((p) => p.vendor_id_ref).filter((id): id is string => !!id))
      ),
      tpaVendorIds: Array.from(new Set(allPolicies.flatMap((p) => p.tpa_vendor_ids ?? []))),
      rxVendorIds: Array.from(new Set(allPolicies.flatMap((p) => p.rx_vendor_ids ?? []))),
      clientIds: Array.from(new Set(allPolicies.map((p) => p.client_id_ref).filter((id): id is string => id !== null))),
    }),
    [allPolicies]
  );

  const { data: vendorsData = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: insuranceProviderIds },
    { enabled: insuranceProviderIds.length > 0 }
  );
  const { data: tpaVendorsData = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: tpaVendorIds },
    { enabled: tpaVendorIds.length > 0 }
  );
  const { data: rxVendorsData = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: rxVendorIds },
    { enabled: rxVendorIds.length > 0 }
  );
  const { data: clientsData = [] } = useGetMany<Client>(
    ResourceName.CLIENTS,
    { ids: clientIds },
    { enabled: clientIds.length > 0 }
  );

  // -- Full cascading: narrow policies by ALL active filters, then derive available choices --
  // Each filter's options are derived from policies that match all OTHER active filters.
  // This ensures you can't select a carrier that doesn't exist for the selected client/TPA/Rx combo.

  /** Normalize a filter value to a string array (handles both string and string[]). */
  const asArray = useCallback((val: string | string[] | undefined): string[] => {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }, []);

  const matchesFilter = useCallback(
    (policy: Policy, excludeSource?: string) => {
      const clientIds = asArray(filters.client_id_ref);
      if (excludeSource !== "client_id_ref" && clientIds.length > 0) {
        if (!policy.client_id_ref || !clientIds.includes(String(policy.client_id_ref))) return false;
      }
      const vendorIds = asArray(filters.vendor_id_ref);
      if (excludeSource !== "vendor_id_ref" && vendorIds.length > 0) {
        if (!policy.vendor_id_ref || !vendorIds.includes(policy.vendor_id_ref)) return false;
      }
      const tpaIds = asArray(filters.tpa_vendor_ids);
      if (excludeSource !== "tpa_vendor_ids" && tpaIds.length > 0) {
        if (!policy.tpa_vendor_ids?.some((id) => tpaIds.includes(id))) return false;
      }
      const rxIds = asArray(filters.rx_vendor_ids);
      if (excludeSource !== "rx_vendor_ids" && rxIds.length > 0) {
        if (!policy.rx_vendor_ids?.some((id) => rxIds.includes(id))) return false;
      }
      return true;
    },
    [filters, asArray]
  );

  // For each filter, compute available choices from policies matching all OTHER filters
  const policyholders = useMemo(() => {
    const matching = allPolicies.filter((p) => matchesFilter(p, "client_id_ref"));
    const ids = new Set(matching.map((p) => String(p.client_id_ref)).filter((id) => id !== "null"));
    const activeClients = clientsData.filter(
      (c) => c.client_status?.toLowerCase() === ClientStatus.ACTIVE && ids.has(String(c.id))
    );
    return activeClients
      .map((c) => ({ id: String(c.id), name: c.client_legal_name || "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPolicies, clientsData, matchesFilter]);

  const carriers = useMemo(() => {
    const matching = allPolicies.filter((p) => matchesFilter(p, "vendor_id_ref"));
    const ids = new Set(matching.map((p) => p.vendor_id_ref).filter(Boolean));
    return vendorsData
      .filter((v) => ids.has(v.id))
      .map((v) => ({ id: v.id, name: v.company_name || "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPolicies, vendorsData, matchesFilter]);

  const tpas = useMemo(() => {
    const matching = allPolicies.filter((p) => matchesFilter(p, "tpa_vendor_ids"));
    const ids = new Set(matching.flatMap((p) => p.tpa_vendor_ids ?? []));
    return tpaVendorsData
      .filter((v) => ids.has(String(v.id)))
      .map((v) => ({ id: String(v.id), name: v.company_name || "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPolicies, tpaVendorsData, matchesFilter]);

  const rxVendors = useMemo(() => {
    const matching = allPolicies.filter((p) => matchesFilter(p, "rx_vendor_ids"));
    const ids = new Set(matching.flatMap((p) => p.rx_vendor_ids ?? []));
    return rxVendorsData
      .filter((v) => ids.has(String(v.id)))
      .map((v) => ({ id: String(v.id), name: v.company_name || "Unknown" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPolicies, rxVendorsData, matchesFilter]);

  // -- Active filter chips --
  const allChoicesMap = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    map.set("client_id_ref", new Map(policyholders.map((c) => [c.id, c.name])));
    map.set("vendor_id_ref", new Map(carriers.map((c) => [c.id, c.name])));
    map.set("tpa_vendor_ids", new Map(tpas.map((c) => [c.id, c.name])));
    map.set("rx_vendor_ids", new Map(rxVendors.map((c) => [c.id, c.name])));
    return map;
  }, [policyholders, carriers, tpas, rxVendors]);

  const activeFilterChips = useMemo(() => {
    const chips: { source: string; label: string; id: string; name: string }[] = [];
    for (const [source, displayLabel] of Object.entries(FILTER_LABELS)) {
      const ids = filters[source];
      if (!ids) continue;
      const parsedIds = Array.isArray(ids) ? ids : [ids];
      const nameMap = allChoicesMap.get(source);
      for (const id of parsedIds) {
        chips.push({ source, label: displayLabel, id, name: nameMap?.get(id) ?? id });
      }
    }
    return chips;
  }, [filters, allChoicesMap]);

  const activeFilterCount = activeFilterChips.length;

  const removeFilter = useCallback(
    (source: string, idToRemove: string) => {
      const params = new URLSearchParams(searchParams);
      let currentFilters: Record<string, string[]> = {};
      try {
        const f = params.get("filter");
        currentFilters = f ? JSON.parse(f) : {};
      } catch {
        currentFilters = {};
      }
      const current = currentFilters[source];
      if (!current) return;
      const updated = Array.isArray(current) ? current.filter((id) => id !== idToRemove) : [];
      if (updated.length > 0) {
        currentFilters[source] = updated;
      } else {
        delete currentFilters[source];
      }
      if (Object.keys(currentFilters).length === 0) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(currentFilters));
      }
      void navigate({ search: params.toString() }, { replace: true });
    },
    [searchParams, navigate]
  );

  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("filter");
    void navigate({ search: params.toString() }, { replace: true });
  }, [searchParams, navigate]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", ml: "auto" }}>
      {/* Active filter chips */}
      {activeFilterChips.map((chip) => (
        <Chip
          key={`${chip.source}-${chip.id}`}
          label={
            <span>
              <Typography component="span" sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
                {chip.label}:{" "}
              </Typography>
              <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                {chip.name}
              </Typography>
            </span>
          }
          size="small"
          onDelete={() => removeFilter(chip.source, chip.id)}
          sx={{ height: 24 }}
        />
      ))}

      {activeFilterCount > 1 && (
        <Chip
          label="Clear all"
          size="small"
          variant="outlined"
          onClick={clearAllFilters}
          sx={{ height: 24, fontSize: "0.7rem" }}
        />
      )}

      {/* Filter button — always rightmost */}
      <Button
        ref={anchorRef}
        size="small"
        variant={activeFilterCount > 0 ? "contained" : "outlined"}
        color={activeFilterCount > 0 ? "primary" : "inherit"}
        startIcon={<FilterIcon sx={{ fontSize: 16 }} />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: "none",
          fontSize: "0.8rem",
          py: 0.5,
          px: 1.5,
          minHeight: 32,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
      </Button>

      {/* Popover panel */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 1, p: 2, width: 320, borderRadius: 2 } } }}
      >
        <Stack spacing={1.5}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Filter by
          </Typography>
          <MultiSelectFilter source="client_id_ref" label="Client" choices={policyholders} />
          <MultiSelectFilter source="vendor_id_ref" label="Carrier" choices={carriers} />
          {tpas.length > 0 && <MultiSelectFilter source="tpa_vendor_ids" label="TPA" choices={tpas} />}
          {rxVendors.length > 0 && <MultiSelectFilter source="rx_vendor_ids" label="Rx/PBM" choices={rxVendors} />}
        </Stack>
      </Popover>
    </Box>
  );
};
